import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { pluralize } from '@/lib/string-utils';
import { cn } from '@/lib/tailwind-utils';
import type { LessonUpdateSummary, LessonUpdateSummaryItem } from '@airreading/common/data';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

export function UpdateLessonSummaryModal({
  open,
  onOpenChange,
  summary,
  onClose,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: LessonUpdateSummary;
  onClose: () => void;
}) {
  const { fullyUpdated, partiallyUpdated } = summary;

  const fullyGroupsLabel = `Fully updated ${fullyUpdated.length} ${pluralize(fullyUpdated.length, 'student group', 'student groups')}`;
  const partiallyGroupsLabel = `Partially updated ${partiallyUpdated.length} ${pluralize(
    partiallyUpdated.length,
    'student group',
    'student groups',
  )}`;

  const fullyLabel = `${fullyGroupsLabel}.`;
  const partiallyLabel = `${partiallyGroupsLabel}.`;

  const handleCopy = () => {
    const sections: string[] = [];

    if (fullyUpdated.length > 0) {
      const lines = [`${fullyGroupsLabel}:`, '', ...fullyUpdated.map((item) => `Class ID: ${item.classId}`)];
      sections.push(lines.join('\n\n'));
    }

    if (partiallyUpdated.length > 0) {
      const lines = [`${partiallyGroupsLabel}:`, '', ...partiallyUpdated.map((item) => `Class ID: ${item.classId}`)];
      sections.push(lines.join('\n\n'));
    }

    navigator.clipboard.writeText(sections.join('\n\n\n'));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" onCloseAutoFocus={onClose}>
        <DialogHeader>
          <DialogTitle>Update lessons summary</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {fullyUpdated.length > 0 && <SummarySection label={fullyLabel} items={fullyUpdated} variant="fully" />}
          {partiallyUpdated.length > 0 && (
            <SummarySection label={partiallyLabel} items={partiallyUpdated} variant="partially" />
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant={'dismiss'} onClick={handleCopy}>
            Copy
          </Button>
          <Button type="button" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummarySection({
  label,
  items,
  variant,
}: {
  label: string;
  items: LessonUpdateSummaryItem[];
  variant: 'fully' | 'partially';
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="rounded-md">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 text-left text-sm font-medium bg-[#F2F2F7] py-2 relative"
        aria-expanded={isOpen}
      >
        <span
          className={cn('absolute left-0 top-1/2 w-1 h-3/4 bg-green-400 -translate-y-1/2', {
            'bg-blue-600': variant === 'partially',
          })}
        />
        <div className="w-full">
          <p>{label}</p>
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {isOpen && (
        <div className="mt-4 max-h-48 overflow-y-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="border px-3 py-2 text-left font-medium">Class ID</th>
                {variant === 'partially' && (
                  <th className="border px-3 py-2 text-left font-medium">Number of sessions not updated</th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.classId}>
                  <td className="border px-3 py-2">{item.classId}</td>
                  {variant === 'partially' && <td className="border px-3 py-2">{item.sessionsNotUpdated}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
